import { ApiClientError } from '@recipes/api-client';
import {
  LOCALES,
  toLocale,
  type MailTemplate,
  type MailTemplateIssue,
  type MailTemplateKey,
  type MailTemplatePreview,
} from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Textarea,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, History, Monitor, RotateCcw, Send, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useBlocker, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/forms/confirm-dialog';
import { toast } from '@/components/feedback/toaster';
import { AdminPage } from '@/features/admin/admin-layout';
import { formatDate } from '@/features/shared/labels';
import { useDebounced } from '@/features/shared/recipe-picker';
import { api } from '@/lib/api';
import { describeError, isNotFound, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

interface Draft {
  subject: string;
  mjml: string;
  text: string;
}

const draftOf = (template: MailTemplate): Draft => ({
  subject: template.subject,
  mjml: template.mjml,
  text: template.text ?? '',
});
const same = (a: Draft, b: Draft) =>
  a.subject === b.subject && a.mjml === b.mjml && a.text === b.text;
const body = (draft: Draft) => ({
  subject: draft.subject,
  mjml: draft.mjml,
  text: draft.text.trim() ? draft.text : null,
});

/** The refusals of the server carry line numbers – show them where the editor is. */
function issuesOf(error: unknown): MailTemplateIssue[] | null {
  if (!(error instanceof ApiClientError)) return null;
  const details = error.error.details as { issues?: MailTemplateIssue[] } | undefined;
  return Array.isArray(details?.issues) ? details.issues : null;
}

/**
 * Admin → E-mail → Templates → one template (docs/partial/email-and-support-spec.md 5.2): source
 * left, preview right. The preview is rendered by the server with the renderer the worker uses,
 * so what is shown here is what goes out.
 */
export function AdminMailTemplateEditorPage() {
  const { t } = useTranslation();
  const { key = '', locale = '' } = useParams();
  const template = useQuery({
    queryKey: queryKeys.admin.mailTemplate(key, locale),
    queryFn: () => api.admin.mail.template(key, locale),
    retry: false,
  });
  return (
    <AdminPage
      section="mailTemplates"
      title={
        template.data
          ? `${t(`admin.mailTemplates.names.${template.data.key}`)} · ${LOCALES[toLocale(locale)].nativeName}`
          : undefined
      }
    >
      <Link
        to="/admin/mail/templates"
        className="mb-4 inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
      >
        <ArrowLeft aria-hidden className="size-4" />
        {t('admin.mailTemplates.back')}
      </Link>
      {template.isLoading && <Spinner label={t('app.loading')} />}
      {template.error && (
        <Alert variant="destructive">
          {isNotFound(template.error)
            ? t('admin.mailTemplates.notFound')
            : describeError(template.error)}
        </Alert>
      )}
      {template.data && (
        <Editor key={`${key}/${locale}/${template.data.version}`} template={template.data} />
      )}
    </AdminPage>
  );
}

function Editor({ template }: { template: MailTemplate }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { key, locale } = template;
  const [saved, setSaved] = useState<Draft>(() => draftOf(template));
  const [draft, setDraft] = useState<Draft>(() => draftOf(template));
  const [issues, setIssues] = useState<MailTemplateIssue[]>([]);
  const [preview, setPreview] = useState<MailTemplatePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [width, setWidth] = useState<600 | 360>(600);
  const [part, setPart] = useState<'html' | 'text'>('html');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const source = useRef<HTMLTextAreaElement>(null);
  const dirty = !same(draft, saved);

  const debounced = useDebounced(draft, 600);
  useEffect(() => {
    const controller = new AbortController();
    setPreviewing(true);
    api.admin.mail
      .preview({ key, locale, ...body(debounced) }, { signal: controller.signal })
      .then((result) => {
        setIssues(result.errors);
        if (result.errors.length === 0) setPreview(result);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setIssues([{ line: null, message: describeError(error) }]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewing(false);
      });
    return () => controller.abort();
  }, [debounced, key, locale]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const accept = (next: MailTemplate) => {
    queryClient.setQueryData(queryKeys.admin.mailTemplate(key, locale), next);
    void queryClient.invalidateQueries({ queryKey: queryKeys.admin.mailTemplates });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.admin.mailTemplateVersions(key, locale),
    });
    setSaved(draftOf(next));
    setDraft(draftOf(next));
    setIssues([]);
  };
  const refuse = (error: unknown) => {
    const found = issuesOf(error);
    if (found) setIssues(found);
    toastError(error);
  };
  const save = useMutation({
    mutationFn: () => api.admin.mail.saveTemplate(key, locale, body(draft)),
    onSuccess: (next) => {
      accept(next);
      toast.success(t('admin.mailTemplates.saved', { version: next.version }));
    },
    onError: refuse,
  });
  const test = useMutation({
    mutationFn: () => api.admin.mail.testTemplate(key, locale, body(draft)),
    onSuccess: (result) =>
      result.ok
        ? toast.success(t('admin.mail.testSent', { to: result.to }))
        : toast.error(`${t('admin.mail.testFailed')} ${result.error ?? ''}`),
    onError: refuse,
  });
  const reset = useMutation({
    mutationFn: () => api.admin.mail.resetTemplate(key, locale),
    onSuccess: (next) => {
      accept(next);
      setResetOpen(false);
      toast.success(t('admin.mailTemplates.resetDone'));
    },
    onError: (e) => toastError(e),
  });

  const insert = (name: string, list: boolean) => {
    const snippet = list ? `{{#each ${name}}}{{this}}<br/>{{/each}}` : `{{${name}}}`;
    const area = source.current;
    const at = area?.selectionStart ?? draft.mjml.length;
    const end = area?.selectionEnd ?? at;
    setDraft({ ...draft, mjml: draft.mjml.slice(0, at) + snippet + draft.mjml.slice(end) });
    requestAnimationFrame(() => {
      area?.focus();
      area?.setSelectionRange(at + snippet.length, at + snippet.length);
    });
  };
  const goTo = (line: number) => {
    const area = source.current;
    if (!area) return;
    const start =
      draft.mjml
        .split('\n')
        .slice(0, line - 1)
        .join('\n').length + (line > 1 ? 1 : 0);
    area.focus();
    area.setSelectionRange(start, start + (draft.mjml.split('\n')[line - 1]?.length ?? 0));
  };

  return (
    <div className="space-y-4" data-testid="mail-template-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={template.customized ? 'default' : 'outline'}
          data-testid="mail-template-state"
        >
          {template.customized
            ? t('admin.mailTemplates.customized', { version: template.version })
            : t('admin.mailTemplates.default')}
        </Badge>
        {dirty && <Badge variant="warning">{t('admin.mailTemplates.unsaved')}</Badge>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHistoryOpen(true)}
            data-testid="mail-template-history"
          >
            <History aria-hidden /> {t('admin.mailTemplates.history')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!template.customized}
            onClick={() => setResetOpen(true)}
            data-testid="mail-template-reset"
          >
            <RotateCcw aria-hidden /> {t('admin.mailTemplates.reset')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            loading={test.isPending}
            disabled={issues.length > 0}
            onClick={() => test.mutate()}
            data-testid="mail-template-test"
          >
            <Send aria-hidden /> {t('admin.mailTemplates.sendTest')}
          </Button>
          <Button
            size="sm"
            loading={save.isPending}
            disabled={!dirty || issues.length > 0}
            onClick={() => save.mutate()}
            data-testid="mail-template-save"
          >
            {t('common.save')}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-3">
          <div>
            <Label htmlFor="template-subject">{t('admin.mailTemplates.subject')}</Label>
            <Input
              id="template-subject"
              className="mt-1"
              value={draft.subject}
              maxLength={200}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              data-testid="mail-template-subject"
            />
          </div>
          <div>
            <Label htmlFor="template-mjml">{t('admin.mailTemplates.source')}</Label>
            <Textarea
              id="template-mjml"
              ref={source}
              className="mt-1 min-h-[24rem] font-mono text-xs leading-5"
              spellCheck={false}
              wrap="off"
              value={draft.mjml}
              onChange={(e) => setDraft({ ...draft, mjml: e.target.value })}
              aria-describedby="template-issues"
              data-testid="mail-template-source"
            />
          </div>
          <div id="template-issues" aria-live="polite">
            {issues.length > 0 && (
              <Alert variant="destructive" data-testid="mail-template-issues">
                <ul className="space-y-1">
                  {issues.map((issue, index) => (
                    <li key={index}>
                      {issue.line !== null && (
                        <button
                          type="button"
                          className="mr-1 font-medium underline underline-offset-2"
                          onClick={() => goTo(issue.line!)}
                        >
                          {t('admin.mailTemplates.line', { line: issue.line })}
                        </button>
                      )}
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
          <details>
            <summary className="cursor-pointer text-sm font-medium">
              {t('admin.mailTemplates.textPart')}
            </summary>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('admin.mailTemplates.textPartHint')}
            </p>
            <Textarea
              className="mt-2 min-h-[8rem] font-mono text-xs"
              spellCheck={false}
              aria-label={t('admin.mailTemplates.textPart')}
              value={draft.text}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
            />
          </details>
          <section aria-labelledby="template-variables">
            <h2 id="template-variables" className="text-sm font-medium">
              {t('admin.mailTemplates.variables')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('admin.mailTemplates.variablesHint')}
            </p>
            <ul className="mt-2 divide-y rounded-md border text-sm">
              {template.variables.map((variable) => (
                <li
                  key={variable.name}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-1 p-2"
                >
                  <button
                    type="button"
                    className="font-mono text-xs text-primary underline-offset-2 hover:underline"
                    onClick={() => insert(variable.name, variable.type === 'list')}
                  >
                    {`{{${variable.name}}}`}
                  </button>
                  {variable.required && (
                    <Badge variant="warning">{t('admin.mailTemplates.required')}</Badge>
                  )}
                  {variable.type === 'url' && (
                    <Badge variant="outline">{t('admin.mailTemplates.link')}</Badge>
                  )}
                  {variable.type === 'list' && (
                    <Badge variant="outline">{t('admin.mailTemplates.list')}</Badge>
                  )}
                  <span className="text-muted-foreground">{variable.description}</span>
                  <span className="w-full truncate text-xs text-muted-foreground">
                    {t('admin.mailTemplates.sample')}{' '}
                    {Array.isArray(variable.sample) ? variable.sample.join(' ⏎ ') : variable.sample}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section aria-labelledby="template-preview" className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 id="template-preview" className="text-sm font-medium">
              {t('admin.mailTemplates.preview')}
            </h2>
            {previewing && <Spinner className="size-4 text-muted-foreground" />}
            <div className="ml-auto flex gap-1">
              <Button
                variant={part === 'html' && width === 600 ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => (setPart('html'), setWidth(600))}
              >
                <Monitor aria-hidden /> {t('admin.mailTemplates.desktop')}
              </Button>
              <Button
                variant={part === 'html' && width === 360 ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => (setPart('html'), setWidth(360))}
              >
                <Smartphone aria-hidden /> {t('admin.mailTemplates.phone')}
              </Button>
              <Button
                variant={part === 'text' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setPart('text')}
              >
                {t('admin.mailTemplates.text')}
              </Button>
            </div>
          </div>
          {preview && (
            <p className="mb-2 truncate text-sm" data-testid="mail-template-preview-subject">
              <span className="text-muted-foreground">{t('admin.mailTemplates.subject')}:</span>{' '}
              <span className="font-medium">{preview.subject}</span>
            </p>
          )}
          <div
            className={cn(
              'overflow-x-auto rounded-lg border bg-muted/40 p-2',
              issues.length > 0 && 'opacity-60',
            )}
          >
            {preview && part === 'html' && (
              <iframe
                title={t('admin.mailTemplates.preview')}
                // No scripts, no forms, no navigation: the mail is looked at, nothing runs.
                sandbox=""
                srcDoc={preview.html}
                className="mx-auto block h-[36rem] rounded bg-paper"
                style={{ width, maxWidth: '100%' }}
                data-testid="mail-template-preview"
              />
            )}
            {preview && part === 'text' && (
              <pre className="max-h-[36rem] overflow-auto whitespace-pre-wrap p-2 text-xs">
                {preview.text}
              </pre>
            )}
          </div>
        </section>
      </div>

      <HistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        templateKey={key}
        locale={locale}
        onRestored={accept}
      />
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title={t('admin.mailTemplates.resetTitle')}
        description={t('admin.mailTemplates.resetDescription')}
        confirmLabel={t('admin.mailTemplates.reset')}
        destructive
        loading={reset.isPending}
        onConfirm={() => reset.mutate()}
      />
      <ConfirmDialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => !open && blocker.state === 'blocked' && blocker.reset()}
        title={t('admin.mailTemplates.leaveTitle')}
        description={t('admin.mailTemplates.leaveDescription')}
        confirmLabel={t('admin.mailTemplates.leave')}
        destructive
        onConfirm={() => {
          if (blocker.state === 'blocked') blocker.proceed();
        }}
      />
    </div>
  );
}

function HistoryDialog({
  open,
  onOpenChange,
  templateKey,
  locale,
  onRestored,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateKey: MailTemplateKey;
  locale: string;
  onRestored: (template: MailTemplate) => void;
}) {
  const { t, i18n } = useTranslation();
  const versions = useQuery({
    queryKey: queryKeys.admin.mailTemplateVersions(templateKey, locale),
    queryFn: () => api.admin.mail.versions(templateKey, locale),
    enabled: open,
  });
  const restore = useMutation({
    mutationFn: (version: number) => api.admin.mail.restoreVersion(templateKey, locale, version),
    onSuccess: (next) => {
      onRestored(next);
      onOpenChange(false);
      toast.success(t('admin.mailTemplates.restored'));
    },
    onError: (e) => toastError(e),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('admin.mailTemplates.history')}</DialogTitle>
          <DialogDescription>{t('admin.mailTemplates.historyDescription')}</DialogDescription>
        </DialogHeader>
        {versions.isLoading && <Spinner label={t('app.loading')} />}
        {versions.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('admin.mailTemplates.historyEmpty')}</p>
        )}
        <ul className="max-h-[50vh] divide-y overflow-y-auto">
          {versions.data?.map((version) => (
            <li key={version.version} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {t('admin.mailTemplates.version', { version: version.version })} ·{' '}
                  {version.subject}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(version.createdAt, i18n.language, true)}
                  {version.createdByName ? ` · ${version.createdByName}` : ''}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                loading={restore.isPending && restore.variables === version.version}
                onClick={() => restore.mutate(version.version)}
                data-testid={`mail-template-restore-${version.version}`}
              >
                {t('admin.mailTemplates.restore')}
              </Button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
